import React, { useState } from "react";
import styles from "./BroadcastPopup.module.scss";
import { ArrowLeft, BannerNotificationIcon, BoxNotificationIcon, ListNotificationIcon, PopUpNotificationIcon, PushNotificationIcon, ScrollNotificationIcon } from "../../../../../assets/icons";
import NotifcationTypeItem from "./NotificationTypeItem";
import Button from "../../../../../common/components/xepp/button/Button";
import Can from "../../../../../guards/Can";
import { UtilityPermissions } from "../../../../../model/permissions/permissions";
import {
    PushNotificationForm,
    PopupNotificationForm,
    ListNotificationForm,
    BannerNotificationForm,
    ScrollAnnouncementForm,
    BoxAnnouncementForm,
    NotificationFormProps,
} from "./forms";
import { BroadcastNotificationPayload } from "./forms/types";
import { POPUP_TABS, PopupTab } from "./forms/PopupNotificationForm";
import { LIST_TABS, ListTab } from "./forms/ListNotificationForm";
import { buildBroadcastFormData } from "./forms/buildBroadcastFormData";
import { useCreateBroadcastNotificationMutation, useEditBroadcastNotificationMutation } from "../../../../../service/notification-service";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../../utils/error-handler";
import FeatureLockedState from "../../../../../common/components/feature-locked-state/FeatureLockedState";
import InfoTooltip from "../../../../../common/components/info-tooltip/InfoTooltip";
import { FeatureFlagName } from "../../../../../tenant/features/featureFlags";
import { useFeatureFlag } from "../../../../../tenant/features/useFeatureFlag";
import { ADMIN_FEATURE_INFO } from "../../../adminFeatureCatalogue";

const BROADCAST_INFO = ADMIN_FEATURE_INFO[FeatureFlagName.ADMIN_BROADCAST];

interface BroadcastPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

interface NotificationType {
    id: number;
    name: string;
    description: string;
    icon: React.ReactElement;
    iconClassName: string;
    form: React.FC<NotificationFormProps>;
    tabs?: readonly string[];
}

const NOTIFICATION_TYPES: NotificationType[] = [
    {
        id: 1,
        name: "Push Notification",
        description: "For sending simple push notification to users device",
        icon: <PushNotificationIcon />,
        iconClassName: "icon-push",
        form: PushNotificationForm,
    },
    {
        id: 2,
        name: "Pop-up Notification",
        description: "For sending notification via a pop-up/bottom sheet when user signs in",
        icon: <PopUpNotificationIcon />,
        iconClassName: "icon-popup",
        form: PopupNotificationForm,
        tabs: POPUP_TABS,
    },
    {
        id: 3,
        name: "List Notification",
        description: "For sending notification direct to users In-app inbox",
        icon: <ListNotificationIcon />,
        iconClassName: "icon-list",
        form: ListNotificationForm,
        tabs: LIST_TABS,
    },
    {
        id: 4,
        name: "Banner Notification",
        description: "For displaying image only pop-up when user signs in",
        icon: <BannerNotificationIcon />,
        iconClassName: "icon-banner",
        form: BannerNotificationForm,
        
    },
    {
        id: 5,
        name: "Scroll Announcement",
        description: "For displaying a ticket announcement text for important reminders",
        icon: <ScrollNotificationIcon />,
        iconClassName: "icon-push",
        form: ScrollAnnouncementForm,
    },
    {
        id: 6,
        name: "Box Announcement",
        description: "For displaying Shot text announcement on a box pop-up",
        icon: <BoxNotificationIcon />,
        iconClassName: "icon-box",
        form: BoxAnnouncementForm,
    },
]

const BroadcastPopup = ({ isOpen, onClose }: BroadcastPopupProps) => {
    const [selectedType, setSelectedType] = useState<NotificationType | null>(null);
    const [popupActiveTab, setPopupActiveTab] = useState<PopupTab>('New Notification');
    const [listActiveTab, setListActiveTab] = useState<ListTab>('New Notification');
    const [isEditMode, setIsEditMode] = useState(false);
    const [createBroadcastNotification, { isLoading }] = useCreateBroadcastNotificationMutation();
    const [editBroadcastNotification, { isLoading: isEditLoading }] = useEditBroadcastNotificationMutation();
    // The type list stays browsable; picking a type shows the locked state instead of its
    // form while the Broadcast feature is off for the utility.
    const broadcastEnabled = useFeatureFlag(FeatureFlagName.ADMIN_BROADCAST);
    const showLocked = selectedType !== null && !broadcastEnabled;

    if (!isOpen) return null;


    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleBack = () => {
        setSelectedType(null);
        setPopupActiveTab('New Notification');
        setListActiveTab('New Notification');
        setIsEditMode(false);
    };

    const handleClose = () => {
        setSelectedType(null);
        setPopupActiveTab('New Notification');
        setListActiveTab('New Notification');
        setIsEditMode(false);
        onClose();
    };

    const handleNotificationEdit = async (notificationId: string, data: BroadcastNotificationPayload) => {
        try {
            await editBroadcastNotification({ notificationId, formData: buildBroadcastFormData(data) }).unwrap();
            toast.success('Broadcast notification updated successfully');
        } catch (e: any) {
            toast.error(getErrorMessage(e?.data));
        }
    };

    const handleNotificationSubmit = async (data: BroadcastNotificationPayload) => {
        try {
            await createBroadcastNotification(buildBroadcastFormData(data)).unwrap();
            toast.success('Broadcast notification sent successfully');
            if (selectedType?.id === 2) {
                setPopupActiveTab('Manage Notification');
            } else if (selectedType?.id === 3) {
                setListActiveTab('Manage Notification');
            }else {
                handleClose();
            }
        } catch (e: any) {
            toast.error(getErrorMessage(e?.data));
        }
    };

    return (
        <div className={styles.backdrop} onClick={handleBackdropClick}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    {selectedType ? (
                        <>
                            <div className={styles.headerRow}>
                                <Button variant="outline" onClick={handleBack} rightIcon={<ArrowLeft />} text="" className={styles.backBtn}/>
                                <div className={styles.headerContainer}>
                                    <div className={styles.titleRow}>
                                        <h2 className={styles.title}>{selectedType.name}</h2>
                                        {showLocked && BROADCAST_INFO && (
                                            <InfoTooltip title={BROADCAST_INFO.title} description={BROADCAST_INFO.description} />
                                        )}
                                    </div>
                                    <span className={styles.subTitle}>View and copy meta data of this transaction</span>
                                </div>
                            </div>
                            {selectedType.tabs && !showLocked && (
                                <div className={styles.headerTabs}>
                                    {selectedType.tabs.map((tab) => {
                                        const isPopup = selectedType.id === 2;
                                        const isActive = isPopup ? popupActiveTab === tab : listActiveTab === tab;
                                        const handleTabClick = isPopup
                                            ? () => setPopupActiveTab(tab as PopupTab)
                                            : () => setListActiveTab(tab as ListTab);
                                        return (
                                            <button
                                                key={tab}
                                                type="button"
                                                className={`${styles.headerTab} ${isActive ? styles.headerTabActive : ''}`}
                                                onClick={handleTabClick}
                                            >
                                                {tab}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <h2 className={styles.title}>Send Broadcast to all Customers</h2>
                            <span className={styles.subTitle}>View all provide information during compliance check</span>
                        </>
                    )}
                </div>
                <div className={styles.body}>
                    {showLocked ? (
                        <FeatureLockedState featureName={BROADCAST_INFO?.title ?? "Broadcast"} />
                    ) : selectedType ? (
                        selectedType.id === 2 ? (
                            <PopupNotificationForm onSubmit={handleNotificationSubmit} activeTab={popupActiveTab} />
                        ) : selectedType.id === 3 ? (
                            <ListNotificationForm onSubmit={handleNotificationSubmit} activeTab={listActiveTab} />
                        ) : (
                            (() => {
                                const Form = selectedType.form;
                                return <Form onSubmit={handleNotificationSubmit} onEdit={handleNotificationEdit} onExistingChange={setIsEditMode} />;
                            })()
                        )
                    ) : (
                        NOTIFICATION_TYPES.map(type => (
                            <NotifcationTypeItem
                                key={type.id}
                                icon={() => type.icon}
                                label={type.name}
                                description={type.description}
                                iconClassName={type.iconClassName}
                                onClick={() => setSelectedType(type)}
                            />
                        ))
                    )}
                </div>
                <div className={styles.footer}>
                    <Button variant="outline" onClick={handleClose} text="Cancel" disabled={isLoading} />
                    {selectedType && !showLocked && (
                        isEditMode ? (
                            <Can permission={UtilityPermissions.EditBroadcastNotification}>
                                <Button type="submit" form="notification-form" text={isEditLoading ? 'Editing...' : 'Edit'} disabled={isLoading || isEditLoading} />
                            </Can>
                        ) : (
                            <Can permission={UtilityPermissions.AddBroadcastNotification}>
                                <Button type="submit" form="notification-form" text={isLoading ? 'Sending...' : 'Send'} disabled={isLoading || isEditLoading} />
                            </Can>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export default BroadcastPopup;
